'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { getErrorMessage } from '@federa/utils/errors/error-core';
import { isPlainObject, isRecordValue } from '@federa/const/record-guards';
import { PAGE_ROUTES } from '@federa/const/routes';
import { AGENT_TOOL_LABEL_BY_NAME } from '@federa/const/agent-status';
import { DESIGN_SYSTEM_UTILITY_CLASSES } from '@federa/const/design-system-utility-classes';
import { FUNCTION_EXECUTION_STATUS } from '@federa/const/function-execution-status';
import { MISSING_VALUE_PLACEHOLDER } from '@federa/const/sentinels';
import { formatCurrency } from '@federa/utils/formatters/currency-core';
import { currentPathname } from '@federa/utils/browser/detection';
import { executeFunctionCall } from '@federa/components/lib/api/functions.execution';
import { CenteredSpinner } from '@federa/components/feedback/CenteredSpinner';
import { SemanticIcon } from '@federa/components/icons/SemanticIcon';
import { useSubmitState } from '@federa/components/hooks/form/useSubmitState';
import { flipped } from '@federa/components/lib/toggle-updater';
import { NAVIGATOR_OFFER_ACTIONS } from '@federa/const/navigator-offer-actions';
import { TITLE_FUNCTION_TITLE } from '@federa/const/title-values';
import { formatYesNo } from '@federa/const/ui-boolean-display';
import { Button } from '../primitives/Button.tsx';
import { colors } from '../css-refs.colors.ts';
import { spacing } from '../css-refs.spacing.ts';
import type { CSSProperties } from 'react';

interface FunctionArgumentKV {
	key: string;
	value: unknown;
}

interface FunctionCardProps {
	functionTitle?: string;
	arguments?: unknown;
	executionId?: string | null;
	status?: string | null;
	explorePath?: string;
}

const MONEY_KEY_RE = /(amount|price|deposit|cost|fee|rent|budget|total|payment|balance)/i;
const STATUS_KEY_RE = /(status|type|category|stage)$/i;
const ID_KEY_RE = /(_id|Id|_ID|reference)$/i;

/** The sentence a failed call shows when its own message says nothing a person can act on. */
const FRIENDLY_FAILURE_FALLBACK = "That didn't work as expected. Feel free to try again or ask Alex for help.";

function getFriendlyErrorMessage(message: string | undefined): string {
	if (!message) return FRIENDLY_FAILURE_FALLBACK;
	if (/Invalid|invalid|required|Missing|must be|cannot be|not allowed|not supported/.test(message)) return message;
	if (/^HTTP \d+:?\s*$/i.test(message)) return FRIENDLY_FAILURE_FALLBACK;
	return message;
}

function extractArguments(input: unknown): FunctionArgumentKV[] {
	if (!isRecordValue(input)) return [];
	if (Array.isArray(input)) {
		// `Array.isArray` narrows to `any[]`, so each element would arrive as `any`; widening the container to `unknown[]`
		// keeps the element guard below (`isPlainObject`) the thing that decides the shape.
		const items: unknown[] = input;
		return items.map((item, idx) => {
			if (isPlainObject(item) && 'key' in item && 'value' in item) return { key: String(item.key), value: item.value };
			return { key: String(idx), value: item };
		});
	}
	const record = input;
	return Object.keys(record).map((k) => ({ key: k, value: record[k] }));
}

function humanizeKey(key: string): string {
	const stripped = key.replace(/[_-]?[Ii]d$/, '');
	const words = stripped
		.replace(/([A-Z])/g, ' $1')
		.replace(/[_-]/g, ' ')
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	const [first, ...rest] = words;
	if (first === undefined) return key;
	return [first.charAt(0).toUpperCase() + first.slice(1).toLowerCase(), ...rest.map((w) => w.toLowerCase())].join(' ');
}

function formatValue(key: string, value: unknown): string {
	if (value == null) return MISSING_VALUE_PLACEHOLDER;
	if (typeof value === 'boolean') return formatYesNo(value);
	if (typeof value === 'number' && MONEY_KEY_RE.test(key)) return formatCurrency(value);
	if (typeof value === 'object') return JSON.stringify(value);
	return String(value);
}

function resolveTitle(rawTitle: string): string {
	const direct = AGENT_TOOL_LABEL_BY_NAME[rawTitle];
	if (direct) return direct;
	return humanizeKey(rawTitle) || rawTitle;
}

interface Categorized {
	primaryAmount: FunctionArgumentKV | null;
	meta: FunctionArgumentKV[];
	body: FunctionArgumentKV[];
	hidden: FunctionArgumentKV[];
}

function categorize(args: FunctionArgumentKV[]): Categorized {
	const result: Categorized = { primaryAmount: null, meta: [], body: [], hidden: [] };
	for (const a of args) {
		const k = a.key;
		const isMoney = typeof a.value === 'number' && MONEY_KEY_RE.test(k);
		if (isMoney && !result.primaryAmount) {
			result.primaryAmount = a;
			continue;
		}
		if (ID_KEY_RE.test(k)) {
			result.hidden.push(a);
			continue;
		}
		if (STATUS_KEY_RE.test(k)) {
			result.meta.push(a);
			continue;
		}
		result.body.push(a);
	}
	return result;
}

function PrimaryAmount({ arg }: { arg: FunctionArgumentKV }) {
	return <p className="text-2xl font-semibold leading-none text-secondary">{formatValue(arg.key, arg.value)}</p>;
}

function MetaLine({ meta }: { meta: FunctionArgumentKV[] }) {
	if (meta.length === 0) return null;
	return (
		<p className="text-xs text-muted-foreground">
			{meta.map((m, idx) => (
				<span key={m.key}>
					{idx > 0 && <span className="mx-1.5">&middot;</span>}
					{formatValue(m.key, m.value)}
				</span>
			))}
		</p>
	);
}

function BodyList({ args }: { args: FunctionArgumentKV[] }) {
	if (args.length === 0) return null;
	return (
		<dl className="space-y-1 text-xs">
			{args.map((a) => (
				<div className="flex items-baseline justify-between gap-3" key={a.key}>
					<dt className="shrink-0 text-muted-foreground">{humanizeKey(a.key)}</dt>
					<dd className="min-w-0 truncate text-right font-medium text-secondary">{formatValue(a.key, a.value)}</dd>
				</div>
			))}
		</dl>
	);
}

/** The disclosure toggle is a bare inline label inside a compact card, so it keeps its zero-padding box and muted
 *  foreground while the `ghost` variant supplies the rest of the control. */
const HIDDEN_DETAILS_TOGGLE_STYLE: CSSProperties = {
	padding: spacing[0],
	gap: spacing[1],
	color: colors.muted.foreground,
};

function HiddenDetails({ hidden, open, onToggle }: { hidden: FunctionArgumentKV[]; open: boolean; onToggle: () => void }) {
	if (hidden.length === 0) return null;
	return (
		<div>
			<Button variant="ghost" size="sm" onClick={onToggle} aria-expanded={open} style={HIDDEN_DETAILS_TOGGLE_STYLE}>
				{open ? 'Hide' : 'Show'} reference IDs
			</Button>
			{open && (
				<dl className="mt-1.5 space-y-1 rounded-lg bg-muted px-2.5 py-2 font-mono text-[11px]">
					{hidden.map((a) => (
						<div className="flex items-baseline justify-between gap-2" key={a.key}>
							<dt className="shrink-0 text-muted-foreground">{humanizeKey(a.key)}</dt>
							<dd className="min-w-0 truncate text-right text-secondary">{formatValue(a.key, a.value)}</dd>
						</div>
					))}
				</dl>
			)}
		</div>
	);
}

function ActionRow({ onDecline, onAccept, title }: { onDecline: () => void; onAccept: () => void; title: string }) {
	return (
		<div className="flex items-center justify-end gap-2">
			<Button variant="outline" size="sm" onClick={onDecline} aria-label={`Decline ${title}`}>
				Decline
			</Button>
			<Button variant="primary" size="sm" onClick={onAccept} aria-label={`Confirm ${title}`}>
				Confirm
			</Button>
		</div>
	);
}

/** Per-tone tokens for the terminal-state badge. Every class string is spelled LITERALLY here rather than composed, because
 * Tailwind v4 detects classes by source text -- an interpolated `border-${tone}-tint` would emit no CSS and render unstyled.
 *
 * DELIBERATELY NOT `satisfies Record<..>`, unlike `WEIGHT_MAP` in `components/typography/Text.tsx`, which took that
 * annotation to make a narrowed union strand a LOUD TS2353 instead of a silent orphan key. The annotation buys nothing
 * here: the index type is `keyof typeof TERMINAL_STATE_TONES`, so the union is DERIVED FROM this map rather than
 * declared independently, and a `Record` written against it would be the union talking to itself -- narrowing the map
 * IS narrowing the union, in one edit, with no second place to fall out of step. FALSIFIER: the day `tone` is retyped
 * to an independently declared union (a `types/ui-*` alias, a D1-derived vocabulary), this decline is void and the
 * annotation is owed, because a member could then be cut somewhere this file cannot see. */
const TERMINAL_STATE_TONES = {
	completed: {
		wrapper: 'inline-flex items-center gap-1.5 rounded-full border border-success-tint bg-success-tint px-2.5 py-1',
		text: 'text-[11px] font-medium text-success',
		icon: 'check',
		iconColor: 'success',
		label: 'Completed',
	},
	declined: {
		wrapper: 'inline-flex items-center gap-1.5 rounded-full border border-muted bg-muted px-2.5 py-1',
		text: 'text-[11px] font-medium text-secondary',
		icon: 'close',
		iconColor: 'secondary',
		label: 'Declined',
	},
} as const;

/** The card's terminal badge; both tones share this markup, so a change to the badge shape is one edit. */
function TerminalState({ title, tone }: { title: string; tone: keyof typeof TERMINAL_STATE_TONES }) {
	const t = TERMINAL_STATE_TONES[tone];
	return (
		<div className={t.wrapper}>
			<SemanticIcon name={t.icon} size="xs" color={t.iconColor} />
			<span className={t.text}>{t.label}</span>
			<span className={DESIGN_SYSTEM_UTILITY_CLASSES.srOnly}>{title}</span>
		</div>
	);
}

function StatusBanner({ message, error }: { message: string | null; error: string | null }) {
	if (!message && !error) return null;
	const isError = Boolean(error);
	return (
		<div
			className={`rounded-lg px-2.5 py-1.5 text-[11px] ${
				isError ? 'border border-error-tint bg-error-tint text-error' : 'border border-success-tint bg-success-tint text-success'
			}`}
		>
			{error || message}
		</div>
	);
}

function CardHeader({ title, primary, meta }: { title: string; primary: FunctionArgumentKV | null; meta: FunctionArgumentKV[] }) {
	return (
		<header className="flex items-start justify-between gap-3">
			<div className="min-w-0">
				<span className="inline-flex items-center gap-1 rounded-full bg-primary-lighter px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary-dark">
					<SemanticIcon name="bolt" size="xs" color="primary" />
					Action required
				</span>
				<h3 className="mt-1 truncate text-sm font-semibold leading-tight text-secondary">{title}</h3>
				<div className="mt-0.5">
					<MetaLine meta={meta} />
				</div>
			</div>
			{primary && (
				<div className="shrink-0 text-right">
					<PrimaryAmount arg={primary} />
				</div>
			)}
		</header>
	);
}

export function FunctionCard({
	functionTitle = TITLE_FUNCTION_TITLE,
	arguments: rawArgs,
	executionId,
	status,
	explorePath = PAGE_ROUTES.EXPLORE,
}: FunctionCardProps) {
	const router = useRouter();
	const submit = useSubmitState();
	const [message, setMessage] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [detailsOpen, setDetailsOpen] = useState(false);
	// executeFunctionCall flips the D1 record, but the `status` PROP never refreshes -- without a local override the card kept showing Confirm/Decline until a reload.
	const [resolvedStatus, setResolvedStatus] = useState<string | null>(null);

	const args = extractArguments(rawArgs);
	const title = resolveTitle(functionTitle);
	const effectiveStatus = resolvedStatus ?? status;
	const isPending = effectiveStatus === FUNCTION_EXECUTION_STATUS.PENDING;
	// 'rejected' was adjudicated DEAD: all three writers of `functions.status` are enumerated in @federa/const/function-execution-status and none emits it.
	const isDeclined = effectiveStatus === FUNCTION_EXECUTION_STATUS.DECLINED;
	const cat = categorize(args);

	async function handleAction(action: 'accept' | 'decline') {
		if (submit.isSubmitting) return;
		setError(null);
		setMessage(null);
		submit.start();
		try {
			const result = await executeFunctionCall({ executionId, functionName: functionTitle, declined: action === 'decline' });
			if (!result.success) {
				setError(getFriendlyErrorMessage(result.message));
			} else {
				// Terminal state locally so the card updates without a reload.
				setResolvedStatus(action === 'decline' ? FUNCTION_EXECUTION_STATUS.DECLINED : FUNCTION_EXECUTION_STATUS.COMPLETED);
				setMessage(action === 'decline' ? 'Declined.' : 'Submitted.');
				if (explorePath && explorePath !== currentPathname('')) {
					router.push(explorePath);
				}
			}
		} catch (e) {
			setError(getFriendlyErrorMessage(getErrorMessage(e)));
		} finally {
			submit.stop();
		}
	}

	if (!isPending) {
		return <TerminalState title={title} tone={isDeclined ? 'declined' : 'completed'} />;
	}

	return (
		<div className="w-full overflow-hidden rounded-lg border border-muted bg-background shadow-sm">
			<div className="space-y-2.5 p-3">
				<CardHeader title={title} primary={cat.primaryAmount} meta={cat.meta} />
				<BodyList args={cat.body} />
				<HiddenDetails hidden={cat.hidden} open={detailsOpen} onToggle={() => setDetailsOpen(flipped)} />
				{submit.isSubmitting ? (
					<CenteredSpinner size="sm" className="py-1" />
				) : (
					<ActionRow
						onDecline={() => handleAction('decline')}
						onAccept={() => handleAction(NAVIGATOR_OFFER_ACTIONS.ACCEPT)}
						title={title}
					/>
				)}
				<StatusBanner message={message} error={error} />
			</div>
		</div>
	);
}
